/**
 * Tenant-configurable date/number formatting for a Studio-driven consumer app.
 *
 * Mirrors the Studio formatter so the customer journey renders dates/numbers/
 * money the same way Studio does. The tenant's preferences arrive from the
 * Studio client API: the form-schema `meta.tenant_format`, and the quote-preview
 * `numberFormat` field.
 *
 * Supported date_format: DD/MM/YYYY · MM/DD/YYYY · YYYY-MM-DD · D MMM YYYY · MMMM D, YYYY
 * Supported time_format: "24h" | "12h"
 * Supported number_format: "1,234.56" | "1.234,56" | "1 234.56"
 */

const MONTHS_SHORT = [
	'Jan',
	'Feb',
	'Mar',
	'Apr',
	'May',
	'Jun',
	'Jul',
	'Aug',
	'Sep',
	'Oct',
	'Nov',
	'Dec',
]
const MONTHS_LONG = [
	'January',
	'February',
	'March',
	'April',
	'May',
	'June',
	'July',
	'August',
	'September',
	'October',
	'November',
	'December',
]

export interface TenantFormat {
	dateFormat: string
	timeFormat: string
	numberFormat: string
	currency?: string
}

export const DEFAULT_TENANT_FORMAT: TenantFormat = {
	dateFormat: 'DD/MM/YYYY',
	timeFormat: '24h',
	numberFormat: '1,234.56',
	currency: '',
}

export interface TenantBranding {
	logoUrl: string
	primaryColor: string
	tagline: string
}

/** Read tenant branding off a form-schema `meta` object (meta.branding). */
export function tenantBrandingFromMeta(meta: any): TenantBranding {
	const b = meta?.branding ?? {}
	return {
		logoUrl: b.logo_url || '',
		primaryColor: b.primary_color || '',
		tagline: b.tagline || '',
	}
}

/** Read tenant format off a form-schema `meta` object (meta.tenant_format). */
export function tenantFormatFromMeta(meta: any): TenantFormat {
	const tf = meta?.tenant_format ?? {}
	return {
		dateFormat: tf.date_format || DEFAULT_TENANT_FORMAT.dateFormat,
		timeFormat: tf.time_format || DEFAULT_TENANT_FORMAT.timeFormat,
		numberFormat: tf.number_format || DEFAULT_TENANT_FORMAT.numberFormat,
		currency: tf.currency || '',
	}
}

function toDate(value: string | number | Date | null | undefined): Date | null {
	if (value === null || value === undefined || value === '') return null
	const d = value instanceof Date ? value : new Date(value)
	return isNaN(d.getTime()) ? null : d
}

export function formatDate(
	value: string | number | Date | null | undefined,
	dateFormat: string = DEFAULT_TENANT_FORMAT.dateFormat
): string {
	const d = toDate(value)
	if (!d) return '—'
	const dd = String(d.getDate()).padStart(2, '0')
	const mm = String(d.getMonth() + 1).padStart(2, '0')
	const yyyy = d.getFullYear()
	switch (dateFormat) {
		case 'MM/DD/YYYY':
			return `${mm}/${dd}/${yyyy}`
		case 'YYYY-MM-DD':
			return `${yyyy}-${mm}-${dd}`
		case 'D MMM YYYY':
			return `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]} ${yyyy}`
		case 'MMMM D, YYYY':
			return `${MONTHS_LONG[d.getMonth()]} ${d.getDate()}, ${yyyy}`
		case 'DD/MM/YYYY':
		default:
			return `${dd}/${mm}/${yyyy}`
	}
}

export function formatTime(
	value: string | number | Date | null | undefined,
	timeFormat: string = DEFAULT_TENANT_FORMAT.timeFormat
): string {
	const d = toDate(value)
	if (!d) return '—'
	const h24 = d.getHours()
	const min = String(d.getMinutes()).padStart(2, '0')
	if (timeFormat === '12h') {
		const period = h24 >= 12 ? 'PM' : 'AM'
		const h12 = h24 % 12 === 0 ? 12 : h24 % 12
		return `${h12}:${min} ${period}`
	}
	return `${String(h24).padStart(2, '0')}:${min}`
}

interface NumberOpts {
	minimumFractionDigits?: number
	maximumFractionDigits?: number
}

function separators(numberFormat: string): { group: string; decimal: string } {
	if (numberFormat === '1.234,56') return { group: '.', decimal: ',' }
	if (numberFormat === '1 234.56') return { group: ' ', decimal: '.' }
	return { group: ',', decimal: '.' }
}

export function formatNumber(
	value: number | string | null | undefined,
	numberFormat: string = DEFAULT_TENANT_FORMAT.numberFormat,
	opts: NumberOpts = {}
): string {
	const n = typeof value === 'string' ? parseFloat(value) : value
	if (n === null || n === undefined || Number.isNaN(n)) return '—'
	const { minimumFractionDigits = 0, maximumFractionDigits = 2 } = opts
	const { group, decimal } = separators(numberFormat)

	const neg = n < 0
	let [intPart = '0', fracPart = ''] = Math.abs(n)
		.toFixed(maximumFractionDigits)
		.split('.')
	while (fracPart.length > minimumFractionDigits && fracPart.endsWith('0')) {
		fracPart = fracPart.slice(0, -1)
	}
	intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, group)
	const out = fracPart ? `${intPart}${decimal}${fracPart}` : intPart
	return neg ? `-${out}` : out
}

/** "<symbol/code><number>" — formats the number per tenant convention. */
export function formatMoney(
	value: number | string | null | undefined,
	prefix: string,
	numberFormat: string = DEFAULT_TENANT_FORMAT.numberFormat,
	opts: NumberOpts = { minimumFractionDigits: 2, maximumFractionDigits: 2 }
): string {
	const n = typeof value === 'string' ? parseFloat(value) : value
	if (n === null || n === undefined || Number.isNaN(n)) return '—'
	return `${prefix}${formatNumber(n, numberFormat, opts)}`
}
